import React, { useState } from 'react';
import { Modal, Button, Label, Checkbox, Alert } from 'flowbite-react';
import { HiDownload, HiExclamationCircle } from 'react-icons/hi';

interface ExportOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: ExportOptions) => void;
  isExporting?: boolean;
}

export interface ExportOptions {
  includeAddresses: boolean;
  format: 'xlsx' | 'csv' | 'json';
}

export const ExportOptionsModal: React.FC<ExportOptionsModalProps> = ({
  isOpen,
  onClose,
  onExport,
  isExporting = false
}) => {
  const [options, setOptions] = useState<ExportOptions>({
    includeAddresses: false,
    format: 'xlsx'
  });

  const handleExport = () => {
    onExport(options);
  };

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="md">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <HiDownload className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            Export Options
          </h3>
        </div>

        <div className="space-y-4">
          {/* Format Selection */}
          <div>
            <Label className="block mb-2 text-sm font-medium text-gray-700">
              Export Format
            </Label>
            <div className="flex gap-4">
              {(['xlsx', 'csv', 'json'] as const).map(format => (
                <label key={format} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    value={format}
                    checked={options.format === format}
                    onChange={() => setOptions({ ...options, format })}
                    className="text-blue-600"
                    disabled={isExporting}
                  />
                  <span className="text-sm text-gray-700 uppercase">{format}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Include Addresses Option */}
          <div className="pt-4 border-t">
            <div className="flex items-start gap-3">
              <Checkbox
                id="includeAddresses"
                checked={options.includeAddresses}
                onChange={(e) => setOptions({ ...options, includeAddresses: e.target.checked })}
                disabled={isExporting}
              />
              <div>
                <Label htmlFor="includeAddresses" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Include home addresses
                </Label>
                <p className="text-xs text-gray-500 mt-1">
                  Add address columns to the export (street, city, state, zip, county)
                </p>
              </div>
            </div>
          </div>

          {/* Warning when addresses are included */}
          {options.includeAddresses && (
            <Alert color="warning" icon={HiExclamationCircle}>
              <div>
                <span className="font-medium">Sensitive Data Warning</span>
                <p className="text-sm mt-1">
                  This export will contain home addresses, which are personally identifiable information (PII).
                  Please handle this data with care and in accordance with data privacy policies.
                </p>
              </div>
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button color="gray" onClick={onClose} disabled={isExporting}>
              Cancel
            </Button>
            <Button onClick={handleExport} disabled={isExporting}>
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
